import React, { useEffect, useState } from "react";
import { Route, Redirect } from "react-router-dom";
import { Spin } from "antd";
import { useSelector } from "react-redux";
import { selectAuthentication, selectGroup } from "../modules/auth/store/authSlice";
import companySettingsApi from "../modules/client/settings/api/companySettingsApi";
import MainLayout from "../layouts/private/MainLayout";
import { UserGroup } from "configs/constants";
import { ProfileProvider } from "layouts/private/ProfileContext";
import { FeatureFlagsProvider } from "layouts/private/FeatureFlagsContext";

/**
 * A route wrapper that checks if the user has a specific entitlement active
 * before allowing access to the route. If not, redirects to a specified path.
 *
 * Usage:
 * <EntitlementProtectedRoute
 *   path="/warehouses"
 *   component={WarehousesPage}
 *   entitlementId="bfda57a5-f264-4984-9740-36eeaff99280"
 *   redirectTo="/home"
 * />
 */
const EntitlementProtectedRoute = ({
  component: Component,
  entitlementId,
  redirectTo = "/home",
  isFullScreen = false,
  ...rest
}) => {
  const authentication = useSelector(selectAuthentication);
  const group = useSelector(selectGroup);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    checkEntitlement();
  }, [entitlementId]);

  if (!authentication || group !== UserGroup.CLIENT) {
    return <Redirect to="/login" />;
  }

  const checkEntitlement = async () => {
    try {
      const entitlementData = await companySettingsApi.getEntitlement(entitlementId);

      // User must have entitlement AND it must be active
      if (entitlementData.hasEntitlement && entitlementData.isActive) {
        setHasAccess(true);
      } else {
        setHasAccess(false);
      }
    } catch (error) {
      console.error("Error checking entitlement:", error);
      // On error, deny access for security
      setHasAccess(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Route
      {...rest}
      render={(props) => {
        if (loading) {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100vh"
              }}
            >
              <Spin size="large" />
            </div>
          );
        }

        if (hasAccess) {
          return (
            <FeatureFlagsProvider>
              <ProfileProvider>
                <MainLayout isFullScreen={isFullScreen}>
                  <Component {...props} />
                </MainLayout>
              </ProfileProvider>
            </FeatureFlagsProvider>
          );
        }

        // No access - redirect
        return <Redirect to={redirectTo} />;
      }}
    />
  );
};

export default EntitlementProtectedRoute;
